import { byId } from './domUtils';
import type { PrototypeRuntime, Root } from './prototypeTypes';
import { refreshTurnstileProviders } from './turnstileRuntime';

export interface TurnstileWidgetCallbacks {
	onSuccess: (token: string) => void;
	onError: () => void;
	onExpired: () => void;
}

const renderGenerations = new WeakMap<HTMLElement, number>();

export function lockSubmitButton(root: Root, btnId: string): void {
	const button = byId(root, btnId) as HTMLButtonElement | null;
	if (!button) return;
	button.disabled = true;
	button.setAttribute('aria-disabled', 'true');
	if (button.hasAttribute('data-transient-action')) button.dataset.idleDisabled = 'true';
	button.setAttribute('data-locked', '');
}

export function unlockSubmitButton(root: Root, btnId: string): void {
	const button = byId(root, btnId) as HTMLButtonElement | null;
	if (!button) return;
	button.disabled = false;
	button.setAttribute('aria-disabled', 'false');
	if (button.hasAttribute('data-transient-action')) button.dataset.idleDisabled = 'false';
	button.removeAttribute('data-locked');
}

export function invalidateTurnstileRender(container: HTMLElement): void {
	renderGenerations.set(container, (renderGenerations.get(container) ?? 0) + 1);
}

export function clearTurnstileWidgetSurface(root: Root, containerId: string, buttonId: string): void {
	const container = byId(root, containerId);
	if (container) {
		invalidateTurnstileRender(container);
		container.replaceChildren();
	}
	lockSubmitButton(root, buttonId);
}

export async function renderTurnstileWidget(
	root: Root,
	runtime: PrototypeRuntime,
	containerId: string,
	btnId: string,
	action: string,
	callbacks: TurnstileWidgetCallbacks,
	recoveryMode: 'inline' | 'external' = 'inline',
): Promise<void> {
	const container = byId(root, containerId);
	if (!container) return;
	const generation = (renderGenerations.get(container) ?? 0) + 1;
	renderGenerations.set(container, generation);
	const isCurrentRender = () => renderGenerations.get(container) === generation;
	const retry = () => renderTurnstileWidget(root, runtime, containerId, btnId, action, callbacks, recoveryMode);
	const fail = () => {
		if (!isCurrentRender()) return;
		lockSubmitButton(root, btnId);
		if (recoveryMode === 'inline') paintInlineTurnstileRecovery(container, retry);
		callbacks.onError();
	};

	lockSubmitButton(root, btnId);
	container.replaceChildren();
	let provider = runtime.interactiveTurnstile;
	if (!provider) {
		try {
			await refreshTurnstileProviders(runtime);
			provider = runtime.interactiveTurnstile;
		} catch {
			// The fail-closed recovery UI below owns this transient failure.
		}
	}
	if (!provider) return fail();

	try {
		await provider.renderWidget(container, action, {
			onSuccess: (token) => {
				if (!isCurrentRender()) return;
				unlockSubmitButton(root, btnId);
				callbacks.onSuccess(token);
			},
			onError: fail,
			onExpired: () => {
				if (!isCurrentRender()) return;
				lockSubmitButton(root, btnId);
				provider?.resetWidget();
				callbacks.onExpired();
			},
		});
	} catch {
		fail();
	}
}

function paintInlineTurnstileRecovery(container: HTMLElement, retry: () => Promise<void>): void {
	const status = document.createElement('div');
	status.className = 'turnstile-inline-recovery';
	status.setAttribute('role', 'status');
	status.setAttribute('aria-live', 'polite');
	const message = document.createElement('span');
	message.textContent = 'Verification could not be loaded. Check your connection.';
	const button = document.createElement('button');
	button.type = 'button';
	button.className = 'auth-turnstile-retry';
	button.textContent = 'Try verification again';
	button.addEventListener('click', () => {
		button.disabled = true;
		void retry();
	});
	status.appendChild(message);
	status.appendChild(button);
	container.replaceChildren(status);
}
